import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageCircle, X, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatBot } from '@/contexts/ChatBotContext'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { QuickSuggestions } from './QuickSuggestions'
import { quickSuggestions } from './config'
import type { ChatMessage as ChatMessageType, ChatState } from '@brochure/shared'

export function ChatBot() {
  const { t, i18n } = useTranslation('common')
  const { isOpen, setIsOpen } = useChatBot()
  const [messages, setMessages] = useState<ChatMessageType[]>([])
  const [chatState, setChatState] = useState<ChatState>('idle')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  const generateId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

  const sendMessage = async (content: string) => {
    if (!content.trim() || chatState === 'loading') return

    const userMessage: ChatMessageType = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setChatState('loading')

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: messages.map((m) => ({ role: m.role, content: m.content })),
          language: i18n.language,
        }),
      })

      const data = await response.json()

      const assistantMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: data.response || data.error || t('chatbot.error'),
        timestamp: new Date(data.timestamp || Date.now()),
      }

      setMessages((prev) => [...prev, assistantMessage])
      setChatState('idle')
    } catch (error) {
      console.error('Chat error:', error)
      setChatState('error')

      const errorMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: t('chatbot.error'),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const handleReset = () => {
    setMessages([])
    setChatState('idle')
  }

  const handleSuggestionSelect = (messageKey: string) => {
    const message = t(messageKey)
    sendMessage(message)
  }

  return (
    <div
      ref={containerRef}
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6"
      style={{ zIndex: 9999 }}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute bottom-16 sm:bottom-20 right-0',
              'w-[calc(100vw-32px)] sm:w-[400px] max-w-[400px]',
              'bg-white shadow-2xl overflow-hidden',
              'flex flex-col',
              'border border-brand-dark/10',
              'border-t-4 border-t-brand-red',
              'rounded-lg',
              'max-h-[calc(100vh-100px)] sm:max-h-[calc(100vh-120px)]'
            )}
          >
            {/* Header */}
            <div className="bg-brand-dark text-white px-5 pt-5 pb-4 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-brand-red font-semibold tracking-[0.2em] text-[10px] uppercase mb-1">
                  {t('chatbot.tagline', 'Assistente virtuale')}
                </p>
                <h3 className="font-bold text-lg leading-tight">
                  {t('chatbot.title')}
                </h3>
                <p className="text-sm text-white/60 mt-1 leading-snug">
                  {t('chatbot.subtitle')}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  onClick={handleReset}
                  className="p-2 hover:bg-white/10 rounded-md transition-colors"
                  aria-label={t('chatbot.newConversation')}
                  title={t('chatbot.newConversation')}
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/10 rounded-md transition-colors lg:hidden"
                  aria-label={t('chatbot.close')}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4 min-h-[220px] max-h-[420px] bg-[#F9F6F1]">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center text-center py-6 px-2">
                  <span className="block w-10 h-[3px] bg-brand-red mb-5" aria-hidden />
                  <p className="text-brand-text text-sm leading-relaxed mb-6 max-w-[280px]">
                    {t('chatbot.welcome')}
                  </p>
                  {quickSuggestions.length > 0 && (
                    <>
                      <p className="text-brand-red text-[10px] font-semibold tracking-[0.2em] uppercase mb-3">
                        {t('chatbot.quickSuggestionsLabel', 'Domande frequenti')}
                      </p>
                      <QuickSuggestions
                        suggestions={quickSuggestions}
                        onSelect={handleSuggestionSelect}
                        disabled={chatState === 'loading'}
                      />
                    </>
                  )}
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <ChatMessage key={message.id} message={message} />
                  ))}
                  {chatState === 'loading' && (
                    <ChatMessage
                      message={{
                        id: 'typing',
                        role: 'assistant',
                        content: '',
                        timestamp: new Date(),
                      }}
                      isTyping
                    />
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput
              ref={inputRef}
              onSend={sendMessage}
              disabled={chatState === 'loading'}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className={cn(
          'relative w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center',
          'bg-brand-red hover:bg-brand-red-dark text-white',
          'shadow-[0_10px_30px_-8px_rgba(227,30,36,0.55)]',
          'transition-colors duration-300',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2'
        )}
        aria-label={isOpen ? t('chatbot.close') : t('chatbot.open')}
      >
        {/* Pulsing halo when closed */}
        <AnimatePresence>
          {!isOpen && (
            <motion.span
              key="halo"
              aria-hidden
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: [0.6, 0, 0.6], scale: [0.9, 1.4, 0.9] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute inset-0 rounded-full bg-brand-red/40"
            />
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative z-10"
            >
              <X className="w-6 h-6 sm:w-7 sm:h-7" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative z-10"
            >
              <MessageCircle className="w-6 h-6 sm:w-7 sm:h-7" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Online dot — only when closed */}
        {!isOpen && (
          <span
            aria-hidden
            className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 z-10 w-2.5 h-2.5 rounded-full bg-white shadow ring-2 ring-brand-red"
          />
        )}
      </motion.button>
    </div>
  )
}
